import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/states';
import { apiMessage } from '../lib/helpers';
import type { LiveTable } from '../lib/live-table';

/** A candidate destination, as the tables list already gives it. */
export interface MoveTarget {
  id: string;
  number: string;
  seats: number | null | undefined;
  state: string;
  areaName: string;
}

interface Props {
  from: LiveTable;
  sessionId: string | null;
  tables: MoveTarget[];
  open: boolean;
  onClose: () => void;
  onMoved: () => void;
}

/**
 * §7 — move a visit to another table.
 *
 * The guests changed tables; the session, its orders and its bill go with them.
 * `POST /sessions/:id/transfer` is the whole operation — nothing is closed and
 * re-opened, so the check keeps its number and the kitchen keeps its tickets.
 *
 * Only free tables are offered. `SessionsService.assertTableAvailable()` is the
 * authority and refuses anything else, so listing an occupied table would be
 * offering a guaranteed error; the count of what was filtered out is shown so
 * an empty list reads as "the floor is full", not "this is broken".
 */
export function MoveSessionDialog({ from, sessionId, tables, open, onClose, onMoved }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const candidates = useMemo(
    () => tables.filter((table) => table.id !== from.id && table.state === 'AVAILABLE'),
    [from.id, tables],
  );

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter(
      (table) =>
        table.number.toLowerCase().includes(needle) ||
        table.areaName.toLowerCase().includes(needle),
    );
  }, [candidates, search]);

  const move = useCallback(
    async (target: MoveTarget) => {
      if (!sessionId) return;
      setBusy(true);
      try {
        await api.post(`/sessions/${sessionId}/transfer`, { tableId: target.id });
        toast.success(t('spaces.move.moved', { from: from.number, to: target.number }));
        onMoved();
        onClose();
      } catch (error) {
        toast.error(apiMessage(error, t('operations.errors.actionFailed')));
      } finally {
        setBusy(false);
      }
    },
    [from.number, onClose, onMoved, sessionId, t],
  );

  const blocked = tables.length - candidates.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('spaces.move.title', { number: from.number })}
      description={t('spaces.move.subtitle')}
      className="w-full max-w-[720px]"
    >
      {!sessionId ? (
        <EmptyState title={t('spaces.session.none')} hint={t('spaces.session.noneHint')} />
      ) : (
        <div className="space-y-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('spaces.move.search')}
            aria-label={t('spaces.move.search')}
          />

          {shown.length === 0 ? (
            <EmptyState
              title={t('spaces.move.noneFree')}
              hint={t('spaces.move.noneFreeHint', { count: Math.max(blocked, 0) })}
            />
          ) : (
            <ul className="max-h-[50vh] space-y-2 overflow-y-auto">
              {shown.map((table) => (
                <li key={table.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void move(table)}
                    className="flex w-full items-center justify-between gap-3 rounded-lg border border-[var(--border)] p-3 text-start hover:border-[var(--primary)] disabled:opacity-50"
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{table.number}</span>
                      <span className="text-sm text-ink-muted">
                        {table.areaName}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-sm text-ink-muted">
                        {t('table.seats', { count: table.seats ?? 0 })}
                      </span>
                      <Badge tone={toneForStatus(table.state)}>
                        {t(`table.state.${table.state}`, table.state)}
                      </Badge>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
}
